import { useCallback, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LineSpinnerIcon from 'LuxKit/Icons/line/LineSpinnerIcon'
import { rotate } from 'components/utils/animations'
import useResizeObserver from 'hooks/useResizeObserver'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import { fillArray } from 'lib/array/arrayUtils'
import zIndex from 'styles/tools/z-index'
import { CHAT_INPUT_INNER_RADIUS } from './ChatInputShell'

interface Props {
  waveformLevels: Array<number>
  filledBars: number
  onCancel: () => void
  onSubmit: () => void
  isTranscribing?: boolean
}

const MIN_BAR_HEIGHT = 18
const MAX_BAR_HEIGHT = 100
const MIN_RENDER_BARS = 24
const MAX_RENDER_BARS = 320
const BAR_WIDTH = 3
const BAR_GAP = 2

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  padding-block: ${rem(12)};
  padding-inline: ${rem(18)} ${rem(12)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  border-radius: ${rem(CHAT_INPUT_INNER_RADIUS)};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  z-index: ${zIndex.min};
  pointer-events: auto;
`

const Waveform = styled.div`
  flex: 1;
  height: ${rem(36)};
  display: flex;
  align-items: center;
  gap: ${rem(BAR_GAP)};
  overflow: hidden;
  position: relative;
`

const WaveformBar = styled.span`
  width: ${rem(BAR_WIDTH)};
  border-radius: ${props => props.theme.borderRadius.round};
  background: ${props => props.theme.palette.neutral.surface.lightForeground};
  opacity: 0.65;
  flex: 0 0 ${rem(BAR_WIDTH)};
  transform-origin: center;
  transition: height 0.12s ease;

  &.active {
    background: ${props => props.theme.palette.neutral.surface.darkForeground};
    opacity: 1;
  }
`

const SpinnerIcon = styled(LineSpinnerIcon)`
  animation: ${rotate} 0.9s linear infinite;
`

function getBarCount(widthPx: number): number {
  // Bars are sized in rem; account for a customised root font size.
  const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize)
  const width = widthPx * 16 / rootFontSize
  const fittingBars = Math.floor((width + BAR_GAP) / (BAR_WIDTH + BAR_GAP))
  return Math.max(MIN_RENDER_BARS, Math.min(MAX_RENDER_BARS, fittingBars))
}

function RecordingOverlay({
  waveformLevels,
  filledBars,
  onCancel,
  onSubmit,
  isTranscribing = false,
}: Props) {
  const intl = useIntl()
  const listeningLabel = intl.formatMessage({
    id: 'krvcq_EYDTW7nHcgWw2V',
    defaultMessage: 'Listening…',
    description: 'Accessible name of the TripPlanner chat voice overlay while the microphone is recording',
  })
  const transcribingLabel = intl.formatMessage({
    id: 'gHbe-vV5EUxi-iIxw4_W',
    defaultMessage: 'Transcribing…',
    description: 'Accessible name of the TripPlanner chat voice overlay while a recording is being turned into text',
  })
  const [renderedBarCount, setRenderedBarCount] = useState(MIN_RENDER_BARS)
  const waveformRef = useRef<HTMLDivElement>(null)

  const updateRenderedBars = useCallback((width: number) => {
    if (width) setRenderedBarCount(getBarCount(width))
  }, [])

  useIsomorphicLayoutEffect(() => {
    if (waveformRef.current) updateRenderedBars(waveformRef.current.getBoundingClientRect().width)
  }, [updateRenderedBars])

  const handleResize = useCallback((entry: ResizeObserverEntry) => {
    updateRenderedBars(entry.contentRect.width)
  }, [updateRenderedBars])
  useResizeObserver(waveformRef, handleResize)

  const adjustedWaveformLevels = useMemo(() => {
    if (renderedBarCount === waveformLevels.length) return waveformLevels
    if (waveformLevels.length === 0) return []

    return fillArray(renderedBarCount, index => waveformLevels[Math.min(
      waveformLevels.length - 1,
      Math.floor((index / renderedBarCount) * waveformLevels.length),
    )])
  }, [renderedBarCount, waveformLevels])

  const firstActiveIndex = useMemo(() => {
    const activeCount = Math.min(
      renderedBarCount,
      Math.max(0, Math.round((filledBars / Math.max(1, waveformLevels.length)) * renderedBarCount)),
    )
    return renderedBarCount - activeCount
  }, [filledBars, renderedBarCount, waveformLevels.length])

  const waveformBars = useMemo(() => adjustedWaveformLevels.map((level, index) => {
    const height = Math.min(
      MAX_BAR_HEIGHT,
      Math.max(MIN_BAR_HEIGHT, MIN_BAR_HEIGHT + level * (MAX_BAR_HEIGHT - MIN_BAR_HEIGHT)),
    )
    // Inline: a styled prop would generate a class per height.
    return <WaveformBar key={`wave-${index}`} className={clsx({ active: index >= firstActiveIndex })} style={{ height: `${height}%` }} />
  }), [adjustedWaveformLevels, firstActiveIndex])

  return (
    <Overlay role="status" aria-label={isTranscribing ? transcribingLabel : listeningLabel}>
      <Waveform aria-hidden ref={waveformRef}>
        {waveformBars}
      </Waveform>
      <IconButton
        kind="secondary"
        shape="circle"
        size="medium"
        aria-label={intl.formatMessage({
          id: '42RE6uT3_lz0oE0LIAEA',
          defaultMessage: 'Cancel recording',
          description: 'Aria-label for the cancel recording button during voice input',
        })}
        onClick={onCancel}
      >
        <LineTimesIcon />
      </IconButton>
      <IconButton
        kind="primary"
        shape="circle"
        size="medium"
        aria-label={intl.formatMessage({
          id: '6lbT8mboL5lraJ8wKaPM',
          defaultMessage: 'Stop recording and transcribe',
          description: 'Aria-label for the stop recording and transcribe button during voice input',
        })}
        onClick={onSubmit}
        disabled={isTranscribing}
        autoFocus
      >
        {isTranscribing ? <SpinnerIcon /> : <LineCheckIcon />}
      </IconButton>
    </Overlay>
  )
}

export default RecordingOverlay
